import {
  Children,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {Box, Heading} from '@primer/react'

import {IdProvider, useListViewId} from './IdContext'
import {ItemsProvider} from './ItemsContext'
import {type SelectionContextProps, SelectionProvider} from './SelectionContext'
import {MultiPageSelectionProvider} from './MultiPageSelectionContext'
import type {Metadata} from './Metadata'

import {useRovingTabIndex} from '../hooks/use-roving-tab-index'
import {VariantProvider, type VariantType} from './VariantContext'
import {defaultVariant} from '../constants'
import {TitleProvider, type TitleProviderProps, useListViewTitle} from './TitleContext'

export type ListViewProps = PropsWithChildren<{
  /**
   * An optional element to contain bulk actions, a 'Select all' checkbox, a density toggle for the view, etc.
   */
  metadata?: ReactElement<typeof Metadata>
  /**
   * Controls the width and height of the list and its contents
   */
  variant?: VariantType
  /**
   * Callback for when the variant changes
   */
  onVariantChange?: (variant: VariantType) => void
  /**
   * Controls item's selection mode; bulk actions are allowed if true. Defaults to not selectable.
   */
  isSelectable?: boolean
  /**
   * What a single list item should be called. Used to customize assistive text about how many list items are
   * selected. Defaults to 'list item'.
   */
  singularUnits?: string
  /**
   * What many list items are called. Used to customize assistive text about how many list items are selected.
   * Defaults to 'list items'.
   */
  pluralUnits?: string
  /**
   * Optional ID of an element that labels the list view. When provided, this takes the place of the default sr-only title.
   */
  ariaLabelledBy?: string

  listRef?: MutableRefObject<HTMLUListElement | undefined>
}> &
  Omit<TitleProviderProps, 'children'> &
  Pick<SelectionContextProps, 'totalCount'>

export const ListView = ({
  title,
  titleHeaderTag,
  children,
  totalCount,
  variant: externalVariant = defaultVariant,
  singularUnits,
  pluralUnits,
  onVariantChange = noop,
  isSelectable,
  ...rest
}: PropsWithChildren<ListViewProps>): JSX.Element => {
  const [variant, setVariant] = useState(externalVariant)

  useEffect(() => setVariant(externalVariant), [externalVariant])

  useEffect(() => {
    onVariantChange?.(variant)
  }, [onVariantChange, variant])

  const countOnPage = useMemo(() => Children.toArray(children).length, [children])

  return (
    <IdProvider>
      <TitleProvider title={title} titleHeaderTag={titleHeaderTag}>
        <VariantProvider variant={variant} setVariant={setVariant}>
          <SelectionProvider
            countOnPage={countOnPage}
            selectedCount={0}
            totalCount={totalCount}
            singularUnits={singularUnits}
            pluralUnits={pluralUnits}
            isSelectable={isSelectable}
          >
            <MultiPageSelectionProvider>
              <ItemsProvider>
                <ListViewContainer {...rest}>{children}</ListViewContainer>
              </ItemsProvider>
            </MultiPageSelectionProvider>
          </SelectionProvider>
        </VariantProvider>
      </TitleProvider>
    </IdProvider>
  )
}

const ulStyles: BetterSystemStyleObject = {listStyle: 'none', margin: 0, padding: 0}

const ListViewContainer = ({
  metadata,
  children,
  listRef,
  ariaLabelledBy: externalAriaLabelledBy,
  ...rest
}: Omit<ListViewProps, 'title' | 'titleHeaderTag'>): JSX.Element => {
  const {idPrefix} = useListViewId()
  const {title, titleHeaderTag} = useListViewTitle()
  const {containerRef} = useRovingTabIndex() as {containerRef: MutableRefObject<HTMLUListElement>}

  useEffect(() => {
    if (listRef) {
      listRef.current = containerRef.current
    }
  }, [containerRef, listRef])

  const listViewContainerTitleId = externalAriaLabelledBy ?? `${idPrefix}-listview-container-title`

  return (
    <div id={`${idPrefix}-listview-container`}>
      {!externalAriaLabelledBy && (
        <Heading className="sr-only" as={titleHeaderTag} id={listViewContainerTitleId}>
          {title}
        </Heading>
      )}
      {/**
       * The `metadata` component might have a title rendered in it as well, so there can be some
       * duplication between it and the sr-only listview-container-title Heading above. However, having that title
       * is important for accessibility and it's better to slightly over-describe than not. The `metadata`
       * section is optional while the title attribute and listview-container-title that renders it are required.
       */}
      {metadata}
      <Box
        as="ul"
        className="list-view-items"
        role="list"
        ref={containerRef}
        sx={ulStyles}
        aria-labelledby={listViewContainerTitleId}
        tabIndex={-1}
        {...testIdProps('list-view-items')}
        {...rest}
      >
        {children}
      </Box>
    </div>
  )
}

try{ ListView.displayName ||= 'ListView' } catch {}
try{ ListViewContainer.displayName ||= 'ListViewContainer' } catch {}